/**
 * Passenger validation utilities.
 *
 * Business rules from API salesRestriction:
 *   - ONE_ADULT_OR_SENIOR_PER_INFANT: infants ≤ adults + senior citizens.
 *   - ONE_ADULT_OR_SENIOR: children require at least 1 adult or senior citizen.
 */

/**
 * Passenger counts keyed by passenger type.
 *
 * Canonical keys (match the API salesRestriction identifiers):
 *   - `adults`          — Adult 17+
 *   - `seniorcitizens`  — Senior 65+
 *   - `children`        — Child 0–16
 *   - `infants`         — Infant 0–3 (no seat, lap only)
 *   - `reducedMobility` — People of Determination (PRM)
 *   - `wheelchair`      — Wheelchair user
 *
 * Using any other key (e.g. `adult` instead of `adults`) silently evaluates
 * as 0 — always use the exact strings above.
 */
export type PassengerCounts = Record<string, number>;

/**
 * Returns the maximum number of infants allowed based on the current passenger
 * selection.
 *
 * Rule: ONE_ADULT_OR_SENIOR_PER_INFANT — only adults and senior citizens
 * determine the infant ceiling. Children (and all other passenger types) are
 * irrelevant.
 */
export function getMaxAllowedInfants(passengers: PassengerCounts): number {
    const adults = Math.max(0, passengers.adults || 0);
    const seniors = Math.max(0, passengers.seniorcitizens || 0);
    return adults + seniors;
}

/**
 * Returns true when the increment button for infants should be disabled.
 *
 * Note: also returns true when no passengers are selected at all (ceiling = 0,
 * infants = 0 → 0 >= 0). To show a warning message, additionally check
 * `(passengers.infants || 0) > 0` so the message does not appear on an empty form.
 */
export function isInfantIncrementDisabled(passengers: PassengerCounts): boolean {
    return (passengers.infants || 0) >= getMaxAllowedInfants(passengers);
}

/**
 * Returns true when the increment button for children should be disabled.
 *
 * Rule: ONE_ADULT_OR_SENIOR — at least 1 adult or senior must be present.
 * This check is unconditional: it does not depend on any other passenger types.
 */
export function isChildIncrementDisabled(passengers: PassengerCounts): boolean {
    const adults = Math.max(0, passengers.adults || 0);
    const seniors = Math.max(0, passengers.seniorcitizens || 0);
    return adults === 0 && seniors === 0;
}
